import { Injectable, inject } from '@angular/core';
import { Apollo, ApolloBase } from 'apollo-angular';
import { ImportedInvoiceMappingStatus, IntegrationProviderEnum, Uuid } from '../../../../../../graphql';
import { GLOBAL_GRAPHQLGATEWAY_NAME_NO_OVERLAY, JOBS_V2_NAME_NO_OVERLAY } from '../../../../../graphql.module';
import { AddImportedInvoiceMapping } from './mutations/AddImportedInvoiceMapping.generated';
import { GetAccountingBillInvoices, GetAccountingBillInvoicesVariables } from './queries/GetAccountingBillInvoices.generated';
import { JobDropdown, JobDropdownVariables } from './queries/JobDropdown.generated';

import { CopyAttachmentsFromExternalInvoices } from '../../../../../data/repositories/jobs/copyAttachmentsFromExternalInvoices.generated';
import { CopyAttachment, CopyAttachmentVariables } from './mutations/CopyAttachment.generated';
import { CategoryWithDescription, CategoryWithDescriptionVariables } from './queries/CategoryWithDescription.generated';
import { CostingItemWithDescription, CostingItemWithDescriptionVariables } from './queries/CostingItemWithDescription.generated';
import { GetInvoiceAttachments, GetInvoiceAttachmentsVariables } from './queries/GetInvoiceAttachments.generated';
import { JobItemsDropdown, JobItemsDropdownVariables } from './queries/JobItemsDropdown.generated';

@Injectable({ providedIn: 'root' })
export class AccountingImportApiService {
    #apollo = inject(Apollo);
    get #gql(): ApolloBase<string> {
        return window.APP_CONFIG.ignoreGraphQlGateway
            ? this.#apollo.use(JOBS_V2_NAME_NO_OVERLAY)
            : this.#apollo.use(GLOBAL_GRAPHQLGATEWAY_NAME_NO_OVERLAY);
    }

    getJobDropdown(variables: JobDropdownVariables) {
        return this.#gql.query({
            query: JobDropdown,
            variables,
            fetchPolicy: 'no-cache',
            errorPolicy: 'all',
        });
    }

    getBillInvoices(variables: GetAccountingBillInvoicesVariables) {
        return this.#gql.query({
            query: GetAccountingBillInvoices,
            variables,
            fetchPolicy: 'no-cache',
            errorPolicy: 'all',
        });
    }
    getJobItemsDropdown(variables: JobItemsDropdownVariables) {
        return this.#gql.query({
            query: JobItemsDropdown,
            variables,
            fetchPolicy: 'no-cache',
            errorPolicy: 'all',
        });
    }
    getCategoryWithDescription(variables: CategoryWithDescriptionVariables) {
        return this.#gql.query({
            query: CategoryWithDescription,
            variables,
            fetchPolicy: 'no-cache',
            errorPolicy: 'all',
        });
    }

    getCostingItemWithDescription(variables: CostingItemWithDescriptionVariables) {
        return this.#gql.query({
            query: CostingItemWithDescription,
            variables,
            fetchPolicy: 'no-cache',
            errorPolicy: 'all',
        });
    }

    addImportedInvoiceMapping(
        provider: IntegrationProviderEnum,
        invoiceId: string,
        status: ImportedInvoiceMappingStatus,
        lineItemId?: string,
        orderId?: Uuid
    ) {
        return this.#gql.mutate({
            mutation: AddImportedInvoiceMapping,
            variables: {
                input: {
                    provider,
                    invoiceId,
                    lineItemId,
                    status,
                    orderId,
                },
            },
            fetchPolicy: 'no-cache',
            errorPolicy: 'all',
        });
    }
    getInvoiceAttachments(variables: GetInvoiceAttachmentsVariables) {
        return this.#gql.query({
            query: GetInvoiceAttachments,
            variables,
            fetchPolicy: 'no-cache',
            errorPolicy: 'all',
        });
    }
    copyAttachment(variables: CopyAttachmentVariables) {
        return this.#gql.mutate({
            mutation: CopyAttachment,
            variables,
            fetchPolicy: 'no-cache',
            errorPolicy: 'all',
        });
    }

    copyAttachmentsFromExternalInvoices(invoiceId: string, provider: IntegrationProviderEnum, purchaseOrderId: Uuid) {
        return this.#gql.mutate({
            mutation: CopyAttachmentsFromExternalInvoices,
            variables: {
                input: {
                    invoiceId,
                    provider,
                    purchaseOrderId,
                },
            },
            fetchPolicy: 'no-cache',
            errorPolicy: 'all',
        });
    }
}
